import type {CmLogger} from "@cm/log"
import type {InletDescriptor, OutletDescriptor} from "#template/runtime-graph/descriptors"
import type {Inlet, Outlet} from "#template/runtime-graph/slots"
import {NotReady} from "#template/runtime-graph/slots"
import type {NodeClassImpl} from "#template/runtime-graph/types"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import type {LoadTextureDescriptor, MaterialConnectionLike, MaterialGraph} from "@cm/material-nodes"
import {
    OutputMaterial,
    getTextureDescriptorForImageResource,
    MaterialConnection,
    TexImage,
    BsdfDiffuse,
    BsdfPrincipled,
    BsdfTranslucent,
    BsdfTransparent,
    BsdfGlass,
    BsdfGlossy,
} from "@cm/material-nodes"

import {defer, map, type Subscription} from "rxjs"
import type {ISceneManager} from "#template/interfaces/scene-manager"
import type {TextureResolution} from "#template/nodes/types"
import type {LodTypeValue} from "#template/value-types"
import type {TypedImageData} from "@cm/utils/typed-image-data"
import {isTypedImageData} from "@cm/utils/typed-image-data"
import type {DataObject} from "@cm/utils"
import {promiseAllProperties} from "@cm/utils"

const TD = TypeDescriptors

export type MaterialMetaDataItem = {
    texImageNode: TexImage
    resolvedData: LoadTextureDescriptor
    slots: Set<string>
}

const getMaterialMetaDataDescriptor: {
    sceneManager: InletDescriptor<ISceneManager>
    lodType: InletDescriptor<LodTypeValue>
    textureResolution: InletDescriptor<TextureResolution>
    materialGraph: InletDescriptor<MaterialGraph>
    logger: InletDescriptor<CmLogger>
    materialMetaData: OutletDescriptor<MaterialMetaDataItem[]>
} = {
    sceneManager: TD.inlet(TD.Identity<ISceneManager>()),
    lodType: TD.inlet(TD.Identity<LodTypeValue>()),
    textureResolution: TD.inlet(TD.Identity<TextureResolution>()),
    materialGraph: TD.inlet(TD.MaterialGraph),
    logger: TD.inlet(TD.Identity<CmLogger>()),
    materialMetaData: TD.outlet(TD.Array(TD.Identity<MaterialMetaDataItem>())),
}

export class GetMaterialMetaData implements NodeClassImpl<typeof getMaterialMetaDataDescriptor, typeof GetMaterialMetaData> {
    static descriptor = getMaterialMetaDataDescriptor
    static uniqueName = "GetMaterialMetaData"
    sceneManager!: Inlet<ISceneManager>
    lodType!: Inlet<LodTypeValue>
    textureResolution!: Inlet<TextureResolution>
    materialGraph!: Inlet<MaterialGraph>
    logger!: Inlet<CmLogger>
    materialMetaData!: Outlet<MaterialMetaDataItem[]>

    private pending: Subscription | null = null

    run() {
        if (
            this.sceneManager === NotReady ||
            this.textureResolution === NotReady ||
            this.lodType === NotReady ||
            this.materialGraph === NotReady ||
            this.logger === NotReady
        ) {
            this.materialMetaData.emitIfChanged(NotReady)
            return
        }

        const {rootNode} = this.materialGraph
        if (!rootNode) {
            this.materialMetaData.emitIfChanged([])
            return
        }

        const texImageNodes: Map<TexImage, Set<string>> = new Map()
        const outputNodes: OutputMaterial[] = []
        type Bsdf = BsdfDiffuse | BsdfPrincipled | BsdfTranslucent | BsdfTransparent | BsdfGlass | BsdfGlossy
        const isBsdfNode = (node: unknown): node is Bsdf => {
            return (
                node instanceof BsdfDiffuse ||
                node instanceof BsdfPrincipled ||
                node instanceof BsdfTranslucent ||
                node instanceof BsdfTransparent ||
                node instanceof BsdfGlass ||
                node instanceof BsdfGlossy
            )
        }

        const bsdfNodes: Bsdf[] = []
        for (const node of rootNode) {
            if (node instanceof TexImage) texImageNodes.set(node, new Set<string>())
            if (node instanceof OutputMaterial) outputNodes.push(node)
            if (isBsdfNode(node)) bsdfNodes.push(node)
        }

        const tagImageUsage = (rootNode: MaterialConnectionLike<unknown> | undefined, slot: string) => {
            if (!rootNode) return
            for (const node of rootNode) {
                if (node instanceof TexImage) {
                    const slots = texImageNodes.get(node)
                    if (slots === undefined) throw new Error("Unexpected undefined usages for TexImage node")
                    slots.add(slot)
                }
            }
        }

        for (const outputNode of outputNodes) {
            tagImageUsage(outputNode.parameters.inputs.displacement, "displacement")
        }

        for (const bsdfNode of bsdfNodes) {
            for (const [inputName, inputValue] of Object.entries(bsdfNode.parameters.inputs)) {
                tagImageUsage(inputValue, inputName)
            }
        }

        const {sceneManager, materialGraph, textureResolution, lodType, logger} = this

        this.pending = this.sceneManager.addTask({
            description: `getMaterialMetaData(${materialGraph.uniqueId})`,
            task: defer(() =>
                Promise.all(
                    [...texImageNodes.entries()].map(async ([texImageNode, slots]) => {
                        if (lodType !== "pathTraced") {
                            const irrelevantInput = new Set<string>([
                                "displacement",
                                "anisotropic",
                                "anisotropicRotation",
                                "tangent",
                                "surfaceMixWeight",
                                "transmissionRoughness",
                                "subsurface",
                                "subsurfaceColor",
                                "subsurfaceRadius",
                                "subsurfaceIor",
                                "subsurfaceAnisotropy",
                            ])
                            if ([...slots].every((input) => irrelevantInput.has(input))) return null
                        }

                        const resolvedData = await (async (): Promise<LoadTextureDescriptor> => {
                            const alphaIsImportant = [...texImageNode.parents].some(
                                (parent) => parent instanceof MaterialConnection && parent.parameters.key === "alpha" && parent.destination !== null,
                            )
                            const resolvedImageResource = await texImageNode.resolveImageResource(sceneManager.getResolver())

                            if (lodType === "pathTraced" && textureResolution === "original" && !isTypedImageData(resolvedImageResource)) {
                                return {
                                    primary: resolvedImageResource,
                                }
                            } else {
                                return getTextureDescriptorForImageResource(
                                    {
                                        textureResolution,
                                        progressiveTextureLoading: texImageNode.parameters.settings.disableProgressiveLoading ?? lodType !== "pathTraced",
                                        logger,
                                    },
                                    resolvedImageResource,
                                    alphaIsImportant,
                                    texImageNode.parameters.settings.forceOriginalResolution ?? false,
                                )
                            }
                        })()

                        const appendDataObjectSize = async (data: DataObject | TypedImageData) => {
                            if (isTypedImageData(data)) {
                                return data
                            } else {
                                const {size} = data

                                if (size === undefined || size === null) {
                                    const size = await sceneManager.getDataObjectSize(data)
                                    return {
                                        ...data,
                                        size,
                                    }
                                } else {
                                    return data
                                }
                            }
                        }

                        return {
                            texImageNode,
                            resolvedData: await promiseAllProperties({
                                primary: appendDataObjectSize(resolvedData.primary),
                                lowRes: resolvedData.lowRes ? appendDataObjectSize(resolvedData.lowRes) : undefined,
                            }),
                            slots,
                        } as MaterialMetaDataItem
                    }),
                ),
            ).pipe(
                map((resolvedData) => {
                    this.materialMetaData.emitIfChanged(resolvedData.filter((item): item is MaterialMetaDataItem => item !== null))
                }),
            ),
            critical: false,
        })
    }

    cleanup() {
        this.pending?.unsubscribe()
        this.pending = null
    }
}
